import { ColumnsWithGroupAndFilteredFields } from "../models/windows/GetColumnsWithGroup";
import {
  CascadeRequiredValue,
  CascadeValue,
} from "../models/analysiscodes/AnalysisCodes";


/**
 * The names a cascade parent field can go by. MechanicalIntegrity and MechIntegrity are the same
 * field spelled two ways across windows, so a mapping stored against one must match the other.
 *
 * Lives here rather than in StoresAccountHelper (which re-exports it for its existing callers)
 * so this module stays a pure helper: StoresAccountHelper reaches the API and the store, and
 * pulling that in made every consumer of the cascade matching depend on the HTTP layer.
 */
export const getParentFieldMappings = (field: string) => {
  const fields = [field];
  if (field === "MechanicalIntegrity") fields.push("MechIntegrity");
  else if (field === "MechIntegrity") fields.push("MechanicalIntegrity");
  return fields;
};

export function extractFieldsAndValues(response: any) {
  let fields: Array<{ [key: string]: any }> = [];
  let values: { [key: string]: string } = {};
  let keys = Object.keys(response.data.fields);
  keys.forEach((key) => {
    const fieldValue = response.data.fields[key];
    fields.push({
      ...fieldValue,
      filteredValues: fieldValue.ValidValues,
    });
    let propName = fieldValue.PropertyName;
    values[propName] = "";
  });
  fields.sort((a, b) => Number(a.FieldOrder) - Number(b.FieldOrder));
  return { fields, values };
}

export function updateCascadeChildOptions(
  child: ColumnsWithGroupAndFilteredFields,
  cascadeValues: CascadeValue[],
  value: string,
  setMetadata: (updater: (prev: any[]) => any[]) => void
) {
  const cascadedChildOptions = cascadeValues.filter(
    (c) => c.ParentValue === value
  );
  const filteredOptions = (child.ValidValues ?? []).filter((value) =>
    cascadedChildOptions.some((child) => value.Code === child.ChildValue)
  );

  const updatedOptions =
    filteredOptions.length > 0 ? filteredOptions : child.ValidValues;

  setMetadata((pre) => {
    return pre.map((p) => {
      if (p.PropertyName === child.PropertyName)
        return {
          ...p,
          filteredValues: updatedOptions,
        } as ColumnsWithGroupAndFilteredFields;
      return p;
    });
  });
}
/**
 * US 906024 - the sentinel the server stores in ancestor_value at the root of a cascade chain,
 * where the parent field has no cascade parent of its own to qualify the rule.
 */
const NO_ANCESTOR = "-EMPTY-";

const trimEnd = (value?: string | null) => (value ?? "").trimEnd();

/**
 * US 906024 - does the currently selected parent code make its cascade child mandatory?
 *
 * Matched on parent field + parent value + ancestor value, exactly as the EAM Web client does
 * (US 873279). The child FIELD is deliberately not part of the match: a field carries a single
 * CascadeChild, so the child in scope is already the one this relationship points at, and
 * matching on a server-side field name that may not spell the same as the mobile PropertyName
 * would silently switch the whole feature off rather than fail loudly.
 */
export function isCascadeChildRequired(
  requiredValues: CascadeRequiredValue[] | undefined,
  parentField?: string,
  parentValue?: string,
  ancestorValue?: string
) {
  if (!requiredValues?.length || !parentField) return false;
  // No code selected means no rule applies - and clearing a code must drop the requirement.
  if (parentValue === null || parentValue === undefined || parentValue === "") {
    return false;
  }

  const parentFieldNames = getParentFieldMappings(trimEnd(parentField)).map(
    (field) => field.toLowerCase()
  );
  const ancestor = ancestorValue ? trimEnd(ancestorValue) : NO_ANCESTOR;
  const parent = trimEnd(String(parentValue));

  return requiredValues.some(
    (rule) =>
      parentFieldNames.includes(trimEnd(rule.ParentField).toLowerCase()) &&
      trimEnd(rule.ParentValue) === parent &&
      trimEnd(rule.AncestorValue) === ancestor
  );
}

/**
 * US 906024 - stamps the cascade "required" state onto the metadata after a cascade parent
 * changes.
 *
 * Called from every path that changes a cascade parent, alongside updateCascadeChildOptions but
 * NOT gated on there being mapped child values: a code with no value mapping at all can still be
 * flagged, which is the originating case for this rule - pick an account and department becomes
 * mandatory, without restricting which departments are allowed.
 *
 * `clearedFields` are the dependants that the caller is blanking as a result of this change;
 * their own cascade requirement no longer has a selected code behind it, so it is dropped in the
 * same pass rather than left stale on the screen.
 */
export function applyCascadeRequired(
  requiredValues: CascadeRequiredValue[] | undefined,
  parentField: string,
  parentValue: string,
  ancestorValue: string | undefined,
  child: ColumnsWithGroupAndFilteredFields | undefined,
  clearedFields: string[],
  setMetadata: (updater: (prev: any[]) => any[]) => void
) {
  const childRequired = isCascadeChildRequired(
    requiredValues,
    parentField,
    parentValue,
    ancestorValue
  );
  const childName = child?.PropertyName;
  const toClear = clearedFields.filter((field) => field !== childName);
  if (!childName && toClear.length === 0) return;

  setMetadata((pre) =>
    pre.map((field) => {
      if (childName && field.PropertyName === childName) {
        return { ...field, CascadeRequired: childRequired };
      }
      if (toClear.includes(field.PropertyName) && field.CascadeRequired) {
        return { ...field, CascadeRequired: false };
      }
      return field;
    })
  );
}

/**
 * US 906024 - the required state a field is actually held to: its own element/window setting
 * OR a cascade rule. Never the other way round, so a normally required field can never be made
 * optional by a field mapping.
 */
export function isFieldRequired(field: {
  IsRequired?: boolean;
  CascadeRequired?: boolean;
}) {
  return !!field?.IsRequired || !!field?.CascadeRequired;
}
